import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Swallow cross-origin "Script error." events before anything else
// wires up. iOS Safari fires these when the OS-level Share sheet
// takes a page snapshot (and various browser extensions inject
// scripts we can't inspect) — the events carry no message, filename,
// stack, or line number, so there's literally nothing debuggable
// about them. The CRA dev-error overlay catches them via `window.
// onerror` and paints a red-screen "Uncaught runtime errors: Script
// error." dialog which is scary for customers and doesn't actually
// indicate a broken app. We register in the capture phase so we
// short-circuit the overlay's own capture-phase listener.
if (typeof window !== "undefined") {
  window.addEventListener(
    "error",
    (e) => {
      const msg = (e && e.message) || "";
      // Also silence the classic cross-origin form where `filename`,
      // `lineno`, and `colno` are all zero — that combo is Safari's
      // sanitized cross-origin error.
      const isCrossOriginSanitized =
        msg === "Script error." ||
        msg === "Script error" ||
        (!msg && !e.filename && !e.lineno && !e.colno);
      if (isCrossOriginSanitized) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the service worker so Chrome / Edge / Samsung Internet
// treat the storefront as an installable PWA on mobile. Without a
// registered SW with a fetch handler, `beforeinstallprompt` never
// fires and the operator's phone never gets the install offer.
//
// We defer the registration until `load` so it never delays the
// first paint. Any failure is silent — the site still works, we
// just lose the install offer on that browser.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .catch(() => {
        /* silent — the site still works without a SW */
      });
  });
}

// Handle PWA taskbar-shortcut clicks (Chrome LaunchQueue API).
// The manifest's `launch_handler.client_mode = "navigate-existing"`
// tells Chrome to reuse this window instead of spawning a new one;
// the launch URL arrives via `window.launchQueue`. We push it into
// history so React Router picks it up as a soft navigation rather
// than a hard reload — same window, new route, no flicker.
if ("launchQueue" in window) {
  window.launchQueue.setConsumer((launchParams) => {
    if (!launchParams || !launchParams.targetURL) return;
    try {
      const u = new URL(launchParams.targetURL);
      const dest = `${u.pathname}${u.search}${u.hash}`;
      // Only intercept same-origin routes; anything else falls
      // through to the browser's default handling.
      if (u.origin === window.location.origin) {
        window.history.pushState({}, "", dest);
        // React Router listens to popstate but not pushState; fire
        // it manually so the router re-renders the new location.
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    } catch {
      /* malformed launch URL — ignore */
    }
  });
}


// iOS Safari auto-zoom prevention — best of both worlds
// -----------------------------------------------------
// The storefront wants two things that traditionally conflict:
//   1. Fit-to-viewport by default (no `maximum-scale=1` needed for
//      normal use, so customers CAN pinch-zoom for accessibility).
//   2. No surprise "zoom in on focus + never zoom back" auto-zoom
//      that iOS Safari fires on form controls.
//
// We resolve both by keeping `maximum-scale=5` in the static meta
// tag (so pinch-zoom works everywhere), then briefly flipping the
// same tag to `maximum-scale=1` for the duration of a form-control
// focus. iOS applies the change immediately on focus (blocking the
// auto-zoom) and immediately on blur (releasing the constraint so
// the customer can pinch-zoom again).
//
// Runs once on load; the listeners are passive and cheap.
if (typeof document !== "undefined") {
  const meta = document.querySelector("meta[name='viewport']");
  if (meta) {
    const relaxed =
      "width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover";
    const locked =
      "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover";
    const shouldLock = (target) => {
      if (!target || target.nodeType !== 1) return false;
      const tag = target.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT") return true;
      if (tag !== "INPUT") return false;
      // Only text-entry inputs trigger iOS auto-zoom. Checkboxes,
      // radios, ranges, files, and colours don't — leaving the
      // relaxed viewport intact for those keeps `space-to-click`
      // behaviour intact on keyboard-driven interactions.
      const t = (target.getAttribute("type") || "text").toLowerCase();
      const textLike = new Set([
        "text",
        "email",
        "password",
        "search",
        "tel",
        "url",
        "number",
        "date",
        "datetime-local",
        "month",
        "week",
        "time",
      ]);
      return textLike.has(t);
    };
    document.addEventListener(
      "focusin",
      (e) => {
        if (shouldLock(e.target)) meta.setAttribute("content", locked);
      },
      true,
    );
    document.addEventListener(
      "focusout",
      (e) => {
        if (shouldLock(e.target)) meta.setAttribute("content", relaxed);
      },
      true,
    );
  }
}
